import {AnimatePresence, motion} from "framer-motion";
import {MiniMap} from "@xyflow/react";
import {Button} from "@/components/ui/Button";
import {Tooltip} from "@/components/ui/tooltip";
import {ChevronDown, Locate, Maximize2, Minimize2} from "lucide-react";
import {TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {Separator} from "@/components/ui/separator";
import {useState} from "react";
import {useReactFlow} from "@xyflow/react";
import {WorkflowNodesData} from "../NodeTypes/NodesData";

function MiniMapNode({
  id,
  x,
  y,
  width,
  height,
  selected,
}: {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  selected: boolean;
}) {
  const {getNodes} = useReactFlow();
  const nodes = getNodes();
  const nodeType = nodes.find(node => node.id === id)?.type;

  const miniMapNodeColor =
    WorkflowNodesData[nodeType]?.styles?.minimapNodeColor;

  const miniMapNodeSelectedColor =
    WorkflowNodesData[nodeType]?.styles?.miniMapNodeSelectedColor;

  return (
    <svg x={x} y={y} className={`block`}>
      <rect
        width={width}
        height={height}
        strokeWidth={10}
        rx={40}
        ry={40}
        fill={selected ? miniMapNodeSelectedColor : miniMapNodeColor}
      />
    </svg>
  );
}

export const Minimap = ({zoomLevel = 100}: {zoomLevel: number}) => {
  const {fitView} = useReactFlow();
  const [isMinimapVisible, setIsMinimapVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="controls"
        initial={{opacity: 0, x: 30}}
        animate={{opacity: 1, x: 0}}
        exit={{opacity: 0, x: 30}}
        transition={{duration: 0.1, ease: "easeInOut"}}
        className="absolute left-5 bottom-5 z-10 shadow-sm border rounded-lg bg-white overflow-hidden w-[200px]"
      >
        <div className="flex items-center justify-between px-2 py-1 gap-2">
          <Tooltip delayDuration={0} disableHoverableContent={true}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => toggleFullscreen()}
              >
                {isFullscreen ? (
                  <Minimize2 />
                ) : (
                  <Maximize2 className="size-6" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={10}>
              <p className="text-sm">Fullscreen</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={0} disableHoverableContent={true}>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-xs" onClick={() => fitView()}>
                <Locate className="size-6" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={10}>
              <p className="text-sm">Fit to Screen</p>
            </TooltipContent>
          </Tooltip>
          <div className="text-sm text-secondary font-medium ml-auto">
            {zoomLevel}%
          </div>
          <Separator className="w-px h-5 bg-border" />
          <Tooltip delayDuration={0} disableHoverableContent={true}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => setIsMinimapVisible(!isMinimapVisible)}
              >
                <motion.div
                  animate={{rotate: isMinimapVisible ? 0 : 180}}
                  transition={{duration: 0.2, ease: "easeInOut"}}
                >
                  <ChevronDown className="size-5" />
                </motion.div>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={10}>
              <p className="text-sm">
                {isMinimapVisible ? "Hide Minimap" : "Show Minimap"}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        <AnimatePresence>
          {isMinimapVisible && (
            <motion.div
              initial={{opacity: 0, height: 0}}
              animate={{opacity: 1, height: "auto"}}
              exit={{opacity: 0, height: 0}}
              transition={{duration: 0.2, ease: "easeInOut"}}
            >
              <MiniMap
                className="!relative !w-full"
                nodeComponent={MiniMapNode}
                maskColor="#e4e4e44a"
                maskStrokeColor="#444444"
                zoomable
                pannable
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};
